<template>
    <v-container class="pa-5" fluid>
        <CarReservationDialog
            :is-open="carReservationDialog"
            :reservation-data="currentReservation"
            @on-close="closeCarReservationDialog"
        />
        <page-title>Samochody</page-title>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <FullCalendar
                        ref="fullCalendar"
                        class="fc fc-ltr fc-unthemed fc-colours"
                        :options="{
                            ...calendarOptions,
                            resources: carReservationsTypes,
                            events: carReservations
                        }"
                    />
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import CarReservationDialog from './CarReservationDialog'

import calendar from '@/mixins/calendar'

import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'

import FullCalendar from '@fullcalendar/vue'
import interaction from '@fullcalendar/interaction'
import resourceTimeline from '@fullcalendar/resource-timeline'
import Tippy from 'tippy.js'
import { appConfig } from '@/config'

export default {
    name: 'CarReservationsList',
    components: {
        PageTitle,
        FullCalendar,
        CarReservationDialog
    },
    mixins: [ calendar ],
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('administration/clearCarReservations')
        next()
    },
    data() {
        return {
            calendarOptions: {
                initialView: 'resourceTimelineDay',
                plugins: [interaction, resourceTimeline],
                schedulerLicenseKey:  appConfig.common.schedulerLicenseKey,
                locale: 'pl',
                resourceAreaColumns: [
                    {
                        headerContent: 'Samochody'
                    }
                ],
                businessHours: {
                    daysOfWeek: [ 1, 2, 3, 4, 5 ],
                    startTime: '08:00',
                    endTime: '16:00',
                },
                headerToolbar: {
                    left: 'prev, next, today',
                    center: 'title',
                    right: 'resourceTimelineDay, resourceTimelineTwoDay, resourceTimelineWeek, next'
                },
                firstDay: 1,

                views: {
                    resourceTimelineTwoDay: {
                        type: 'resourceTimeline',
                        duration: { days: 2 },
                        buttonText: '2 dni'
                    }
                },
                customButtons: {
                    prev: {
                        text: 'prev',
                        click: () => this.getDataOnWeekChangeButton('prev')
                    },
                    next: {
                        text: 'next',
                        click: () => this.getDataOnWeekChangeButton('next')
                    },
                    today: {
                        text: 'Dziś',
                        click: () => this.getTodaysReservations()
                    }
                },
                buttonText: {
                    today:    'Dziś',
                    week:     'Tydzień',
                    day:      'Dzień',
                },
                height: 'auto',
                selectable: true,
                dateClick: this.showCarReservationDialog,
                eventClick: this.showEditCarReservationDialog,
                eventDidMount: this.eventRender,
                slotDuration: '00:30:00',
                resourceAreaWidth: '15%',
                scrollTime: '05:00'
            },
            currentReservation: {
                actionName: null,
                info: null,
                reservationData: {}
            },
            carReservationDialog: false,
        }
    },
    computed: {
        carReservations() {
            return this.$store.state.administration.carReservations.carReservations
        },
        carReservationsTypes() {
            return this.$store.state.administration.carReservations.carReservationsTypes
        },
    },
    mounted() {
        this.getCarReservations(this.currentWeekDateRange())
        Promise.all([
            this.$store.dispatch('administration/getCarReservationsTypes'),
            this.$store.dispatch('administration/getCarReservationsStatuses')
        ])
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        getCarReservations(weekRange) {
            this.$store.dispatch('administration/getCarReservations', {
                query: `?startDate=${weekRange[0]}&endDate=${weekRange[weekRange.length-1]}`
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getTodaysReservations() {
            const fullCalendarApi = this.$refs.fullCalendar.getApi()

            fullCalendarApi.today()
            this.getCarReservations(this.currentWeekDateRange())
        },
        getDataOnWeekChangeButton(direction) {
            this.$store.dispatch('administration/clearCarReservations')
            const fullCalendarApi = this.$refs.fullCalendar.getApi()

            direction === 'next' ? fullCalendarApi.next() : fullCalendarApi.prev()
            const start = fullCalendarApi.view.activeStart
            const weekRange = this.generateWeekRange(start)

            this.getCarReservations(weekRange)
        },
        updateCalendarView() {
            const fullCalendarApi = this.$refs.fullCalendar.getApi()
            const start = fullCalendarApi.view.activeStart
            const weekRange = this.generateWeekRange(start)

            this.getCarReservations(weekRange)
        },
        showCarReservationDialog(info) {
            this.currentReservation = {
                actionName: 'create',
                info: info
            }
            this.carReservationDialog = hasUserPermission('car_reservations_create')
        },
        showEditCarReservationDialog(info) {
            this.$store.dispatch('administration/getCarReservation', info.event.id)
                .then(response => {
                    this.currentReservation = {
                        actionName: 'update',
                        info: info,
                        reservationData: response.data
                    }
                    this.carReservationDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeCarReservationDialog() {
            this.carReservationDialog = false
            setTimeout(() => {
                this.currentReservation = {
                    actionName: null,
                    info: null,
                    reservationData: {}
                }
            }, 500)
            this.updateCalendarView()
        },
        eventRender(info) {
            const content = `
                <div class="v-application">
                    <div class="d-block">
                        <div class="mb-1 subtitle font-weight-bold">
                            ${info.event.extendedProps.description.reservationName}
                        </div>
                        <div>
                            Termin: <span class="font-weight-bold">${info.event.extendedProps.description.reservationDate}</span>
                        </div>
                        <div>
                            Rezerwujący:
                            <span class="font-weight-bold">${info.event.extendedProps.description.reservationCreatedBy}</span>
                        </div>
                        <div>
                            <label>Status:</label>
                            <span class="font-weight-bold">${info.event.extendedProps.description.reservationStatus}</span>
                        </div>
                    </div>
                </div>
            `

            Tippy(info.el, {
                content: content,
                placement: 'top',
                theme: 'grey',
                allowHTML: true
            })
        },
    }
}

</script>
